import React, { useMemo, useState, useCallback, useEffect } from "react";
import clsx from "clsx";

import "./index.scss";
import UserListTable from "./components/UserListTable";
import UserFormModal from "./components/UserFormModal";
import CustomSelect from "../Shared/CustomSelect/CustomSelect";
import UserCircleIcon from "../../img/account_circle.svg";
import AddIcon from "../../img/add.svg";
import SearchBar from "../NewReport/Components/searchBar";
import { useNotification } from "../Notifications/NotificationProvider";
import { USER_STATUS_LIST } from "./utils/userConfigs";
import {
  getCreateEditRoleOptions,
  getRoleFilterConfig,
} from "./utils/userHelper";
import { userDetailsFetcher, userService } from "./services/userService";
import StatusActionModal from "./components/StatusActionModal";
import useQueryParams from "../Shared/Hooks/useQueryParms";
import useDebounce from "../Shared/GeographyFilters/hooks/useDebounce";
import { Redirect } from "react-router";

const UserList = () => {
  const [showUserCreateModal, setShowUserCreateModal] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState({
    count: 0,
    editedUserId: null,
  });
  const [isEditMode, setIsEditMode] = useState(false);
  const [editUserData, setEditUserData] = useState(null);
  const [isEditDataLoading, setIsEditDataLoading] = useState(false);
  const [statusActionModal, setStatusActionModal] = useState({
    isOpen: false,
    user: null,
    command: "",
  });
  const [isStatusUpdating, setIsStatusUpdating] = useState(false);
  const [resigningDate, setResigningDate] = useState("");

  const authUserType = JSON.parse(localStorage.getItem("currUserType")) || "";
  const dispatchNotification = useNotification();

  const roleFilterConfig = useMemo(
    () => getRoleFilterConfig(authUserType),
    [authUserType]
  );

  const statusValues = useMemo(
    () => USER_STATUS_LIST.map((status) => status.value),
    []
  );
  const roleValues = useMemo(
    () => roleFilterConfig.filterOptions.map((option) => option.value),
    [roleFilterConfig]
  );
  const { params, setParams } = useQueryParams({
    status: {
      defaultValue: "",
      permittedValues: statusValues,
    },
    user_type: {
      defaultValue: roleFilterConfig.defaultValue || "",
      permittedValues: roleValues,
    },
    query: {
      defaultValue: "",
    },
  });

  const [selectedTab, setSelectedTab] = useState(() => {
    if (USER_STATUS_LIST.some((status) => status.value === params.status)) {
      return params.status;
    }
    return "";
  });

  const [selectedUserType, setSelectedUserType] = useState(() => {
    if (
      roleFilterConfig.filterOptions.some(
        (option) => option.value === params.user_type
      )
    ) {
      return params.user_type;
    }
    return roleFilterConfig.defaultValue || "";
  });

  const [searchTerm, setSearchTerm] = useState(() => {
    return params.query || "";
  });
  const debouncedSearchTerm = useDebounce(searchTerm, 400);

  const roleOptions = useMemo(
    () =>
      roleFilterConfig.filterOptions.map((role) => ({
        id: role.value,
        name: role.name,
      })),
    [roleFilterConfig]
  );

  const createEditRoleOptions = useMemo(
    () => getCreateEditRoleOptions(roleFilterConfig.filterOptions),
    [roleFilterConfig]
  );

  const handleStatusChange = useCallback(
    (status) => {
      if (USER_STATUS_LIST.some((s) => s.value === status)) {
        setSelectedTab(status);
        setParams({ status, query: "" });
      } else {
        setSelectedTab("");
        setParams({ status: "", query: "" });
      }
      setSearchTerm("");
    },
    [setParams]
  );

  const handleUserTypeChange = useCallback(
    (userType) => {
      const defaultUserType = roleFilterConfig.defaultValue || "";
      if (roleOptions.some((option) => option.id === userType)) {
        setSelectedUserType(userType);
        setParams({ user_type: userType, query: "" });
      } else {
        setSelectedUserType(defaultUserType);
        setParams({ user_type: defaultUserType, query: "" });
      }
      setSearchTerm("");
    },
    [roleOptions, setParams, roleFilterConfig.defaultValue]
  );

  const handleSearchChange = useCallback((e) => {
    const search = e.target.value;
    setSearchTerm(search);
  }, []);

  useEffect(() => {
    const currentQuery = params.query || "";
    if (currentQuery !== debouncedSearchTerm) {
      setParams({ query: debouncedSearchTerm });
    }
  }, [debouncedSearchTerm, params.query, setParams]);

  const handleAddNewUser = () => {
    setIsEditMode(false);
    setEditUserData(null);
    setIsEditDataLoading(false);
    setShowUserCreateModal(true);
  };

  const handleEditUser = async (userId) => {
    setIsEditMode(true);
    setEditUserData(null);
    setIsEditDataLoading(true);
    setShowUserCreateModal(true);

    try {
      const userData = await userDetailsFetcher(userId);
      setEditUserData(userData || null);
    } catch (error) {
      setShowUserCreateModal(false);
      setIsEditMode(false);
      setEditUserData(null);
      alert(error.message || "Failed to load user details");
    } finally {
      setIsEditDataLoading(false);
    }
  };

  const handleModalClose = () => {
    setIsEditMode(false);
    setEditUserData(null);
    setIsEditDataLoading(false);
  };

  const handleUserCreated = () => {
    setRefreshTrigger((prev) => ({
      count: prev.count + 1,
      editedUserId: isEditMode ? editUserData?.id || null : null,
    }));
  };

  const handleStatusActionClick = useCallback((user) => {
    if (!user || !user.id) {
      return;
    }

    setStatusActionModal({
      isOpen: true,
      user,
      command: user.is_active ? "deactivate" : "activate",
    });
    setResigningDate("");
  }, []);

  const handleStatusModalClose = useCallback(() => {
    if (isStatusUpdating) {
      return;
    }

    setStatusActionModal({
      isOpen: false,
      user: null,
      command: "",
    });
    setResigningDate("");
  }, [isStatusUpdating]);

  const handleConfirmStatusAction = useCallback(async () => {
    if (
      !statusActionModal.user ||
      !statusActionModal.user.id ||
      !statusActionModal.command
    ) {
      return;
    }

    setIsStatusUpdating(true);
    try {
      await userService.activateDeactivateUser({
        userId: statusActionModal.user.id,
        command: statusActionModal.command,
        resigningDate:
          statusActionModal.command === "deactivate"
            ? resigningDate || null
            : null,
      });

      dispatchNotification({
        type: "SUCCESS",
        message: `${
          statusActionModal.command === "activate" ? "Activated" : "Deactivated"
        } ${statusActionModal.user.name || "user"} successfully`,
      });

      setRefreshTrigger((prev) => ({
        count: prev.count + 1,
        editedUserId: statusActionModal.user?.id || null,
      }));
      setStatusActionModal({
        isOpen: false,
        user: null,
        command: "",
      });
      setResigningDate("");
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        `Failed to ${statusActionModal.command} user`;

      dispatchNotification({
        type: "ERROR",
        message: errorMessage,
      });
    } finally {
      setIsStatusUpdating(false);
    }
  }, [statusActionModal, resigningDate, dispatchNotification]);

  const localUserToken = JSON.parse(localStorage.getItem("currUserToken"));
  if (!localUserToken) return <Redirect to="/signin" />;

  return (
    <div className="main-container">
      <div className="page-header">
        <span className="page-header-title">User Management - User List</span>
      </div>
      <div className="page-content-container">
        <div className="page-content">
          <div className="user-list-filters">
            <div className="user-role-filter">
              <CustomSelect
                item={{
                  key: "user_type",
                  options: roleOptions,
                  value: selectedUserType,
                  defaultSelected: roleFilterConfig.defaultValue || "",
                  label: "Filter by Role",
                  icon: UserCircleIcon,
                }}
                style={{
                  border: "1px solid #362B7314",
                  boxShadow: "none",
                  width: "280px",
                }}
                placeholder="Filter by Role"
                handleChange={(event) => {
                  handleUserTypeChange(event.target.value || "");
                }}
                showExpandIndicator={true}
                isSearchable={false}
              />
            </div>
            <div className="search-and-add-user">
              <div className="user-search-input">
                <SearchBar value={searchTerm} onChange={handleSearchChange} />
              </div>
              <button className="create-btn" onClick={handleAddNewUser}>
                <span>Add New User </span>
                <img src={AddIcon} alt="Add Icon" className="btn-icon" />
              </button>
            </div>
          </div>
          <div className="user-status-tabs-container">
            {USER_STATUS_LIST.map((tab) => (
              <button
                key={tab.id}
                className={clsx(
                  "user-status-tab",
                  selectedTab === tab.value && "active"
                )}
                onClick={() => handleStatusChange(tab.value)}
              >
                {tab.title}
              </button>
            ))}
          </div>
          <div className="user-list-table-container">
            <UserListTable
              searchTerm={debouncedSearchTerm}
              status={selectedTab}
              userType={selectedUserType}
              refreshTrigger={refreshTrigger}
              onEditUser={handleEditUser}
              onStatusActionClick={handleStatusActionClick}
            />
          </div>
        </div>
      </div>

      <UserFormModal
        showUserCreateModal={showUserCreateModal}
        setShowUserCreateModal={setShowUserCreateModal}
        onUserCreated={handleUserCreated}
        isEditMode={isEditMode}
        editUserData={editUserData}
        isEditDataLoading={isEditDataLoading}
        onModalClose={handleModalClose}
        prefilledUserType={selectedUserType}
        roleOptions={createEditRoleOptions}
      />

      <StatusActionModal
        statusActionModal={statusActionModal}
        isStatusUpdating={isStatusUpdating}
        onClose={handleStatusModalClose}
        onConfirm={handleConfirmStatusAction}
        resigningDate={resigningDate}
        onResigningDateChange={setResigningDate}
      />
    </div>
  );
};

export default UserList;
